import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  MutationError,
  latestError,
  Skeleton,
} from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { usePartyPrincipal } from "../auth/party-principal";
import { useConsents, useGrantConsent, useNotices, useWithdrawConsent } from "./use-consents";
import type { ConsentChannel, ConsentRow, Notice } from "../api/contracts";

// TF2 — the consent governance surface (the KVKK promise as a control panel). Renders
// the versioned consent ledger (the frozen 7-step model), grants/withdraws any one
// ONE-TAP behind fresh-auth (stepUp), and holds the no-spam panel (service always-on;
// marketing consent-gated, off by default, per-channel). Masked-only, computes nothing:
// it renders the reads and submits grant/withdraw intents (ADR 0005 §5 / ADR 0016 §6).

type TFn = (k: MessageKey) => string;

const MARKETING_PURPOSE = "marketing";
// SMS / e-posta / arama — the İYS marketing channels the customer opts into per-channel.
// PUSH exists in the schema but is not a v1 no-spam surface (no app-push yet).
const MARKETING_CHANNELS: ConsentChannel[] = ["SMS", "EMAIL", "PHONE"];

// The canonical KVKK sequence (ADR 0016 §6). `health` is health-gated — hidden for
// non-health products (Customer #1's trafik). `required` consents are captured UPSTREAM
// at quote-intake (the Public Web surface, not built) — here the customer REVIEWS their
// state; `marketing` is the live per-channel interaction, governed in the panel below.
type StepKind = "required" | "health" | "marketing" | "optional";
// `noticeCode` binds each step to its governed notice (packages/db/src/seed/
// consents-v1.data.ts), which is how a step's state is resolved from the REAL consent
// ledger instead of being asserted.
const STEPS: { key: string; kind: StepKind; noticeCode: string }[] = [
  { key: "notice", kind: "required", noticeCode: "aydinlatma-notice" },
  { key: "general", kind: "required", noticeCode: "genel-acik-riza" },
  { key: "health", kind: "health", noticeCode: "saglik-acik-riza" }, // gated; absent for trafik
  { key: "terms", kind: "required", noticeCode: "hizmet-kosullari" },
  { key: "distance", kind: "required", noticeCode: "mesafeli-on-bilgilendirme" },
  { key: "marketing", kind: "marketing", noticeCode: "iys-pazarlama" }, // per-channel, unticked
  { key: "cookie", kind: "optional", noticeCode: "cerez" },
];

function asOf(iso: string): string {
  // Display-only "as of" — the server owns the timestamp; the browser just formats it.
  return new Date(iso).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function purposeLabel(purposeId: string, t: TFn): string {
  // Known purposes get a friendly label; anything else falls back to its raw code (never
  // a broken key). Customer #1's live purpose is marketing; the rest are captured upstream.
  const known: Record<string, MessageKey> = { marketing: "cs.purpose.marketing" };
  const key = known[purposeId];
  return key ? t(key) : purposeId;
}

export function Consents({ hasHealth = false }: { hasHealth?: boolean }) {
  const { t } = useT();
  const { profile } = usePartyPrincipal();
  const notices = useNotices();
  const consents = useConsents();
  const grant = useGrantConsent(profile?.partyId ?? "");
  const withdraw = useWithdrawConsent();

  if (notices.isLoading || consents.isLoading) {
    return (
      <Card data-testid="consents-loading">
        <Skeleton lines={6} />
      </Card>
    );
  }
  if (notices.error || consents.error) {
    return (
      <ErrorState>
        <p data-testid="consents-error">{t("cs.error")}</p>
      </ErrorState>
    );
  }

  const noticeList = notices.data?.data ?? [];
  const consentList = consents.data?.data ?? [];
  const noticeById = new Map(noticeList.map((n) => [n.id, n]));
  // The İYS/pazarlama notice the marketing grant references (versioned). Fall back to the
  // first notice on mock; the required-consent capture (Public Web) owns the canonical map.
  const marketingNotice = noticeList.find((n) => /market|pazar|iys/i.test(n.code)) ?? noticeList[0];
  const active = consentList.filter((c) => c.status === "GRANTED");
  // Every notice this party has ANY row against (incl. withdrawn) — a withdrawn health
  // consent must stay visible, or the subject loses sight of a record we still hold.
  const ledgerNoticeCodes = new Set(
    consentList
      .map((c) => noticeById.get(c.noticeId)?.code)
      .filter((code): code is string => !!code),
  );
  // `hasHealth` was NEVER passed by the single call site (App.tsx), so the health
  // açık-rıza step was permanently filtered out and a customer who had granted it could
  // not see or withdraw it. The prop is kept for a caller that knows a health context is
  // coming (a health quote flow), but the ledger itself is now the default source.
  const showHealth = hasHealth || ledgerNoticeCodes.has("saglik-acik-riza");
  const steps = STEPS.filter((s) => s.kind !== "health" || showHealth);
  // notice code → whether the ledger holds a GRANTED row against it. The join goes
  // through noticeId because a consent row references the versioned notice, not a code.
  const grantedNoticeCodes = new Set(
    active.map((c) => noticeById.get(c.noticeId)?.code).filter((code): code is string => !!code),
  );
  const busy = grant.isPending || withdraw.isPending;

  return (
    <div className="stack" data-testid="consents">
      <header className="stack-tight">
        <h1>{t("cs.title")}</h1>
        <p className="muted">{t("cs.subtitle")}</p>
      </header>

      {/* ── The 7-step model (the presentation) — unbundled, unticked, health-gated ── */}
      <Card data-testid="seven-step">
        <h2>{t("cs.stepsTitle")}</h2>
        <ol className="consent-steps">
          {steps.map((s) => (
            <li key={s.key} data-testid={`step-${s.key}`} className="consent-step">
              <span className="consent-step-label">{t(`cs.step.${s.key}` as MessageKey)}</span>
              <StepState kind={s.kind} granted={grantedNoticeCodes.has(s.noticeCode)} t={t} />
            </li>
          ))}
        </ol>
        <p className="muted small">{t("cs.captureNote")}</p>
      </Card>

      {/* ── The consent ledger — the granted consents, versioned + "as of" ── */}
      <Card data-testid="consent-ledger">
        <h2>{t("cs.ledgerTitle")}</h2>
        {active.length === 0 ? (
          <EmptyState>
            <p data-testid="ledger-empty">{t("cs.ledgerEmpty")}</p>
          </EmptyState>
        ) : (
          <ul className="consent-ledger">
            {active.map((c) => (
              <LedgerRow
                key={c.id}
                row={c}
                notice={noticeById.get(c.noticeId)}
                t={t}
                busy={busy}
                onWithdraw={() => withdraw.mutate(c.id)}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* ── The no-spam control panel — the differentiator ── */}
      <Card data-testid="nospam-panel">
        <h2>{t("cs.nospamTitle")}</h2>

        {/* Service / renewal / claim contact — always on, needs no marketing consent. */}
        <div className="nospam-row" data-testid="nospam-service">
          <div>
            <strong>{t("cs.serviceTitle")}</strong>
            <p className="muted small">{t("cs.serviceBody")}</p>
          </div>
          <Badge tone="success">{t("cs.alwaysOn")}</Badge>
        </div>

        {/* Marketing — consent-gated, OFF by default, per channel. */}
        <div data-testid="nospam-marketing">
          <p className="muted small">{t("cs.marketingBody")}</p>
          {MARKETING_CHANNELS.map((ch) => {
            const granted = active.find(
              (c) => c.purposeId === MARKETING_PURPOSE && c.channel === ch,
            );
            return (
              <div className="nospam-row" data-testid={`channel-${ch}`} key={ch}>
                <span>{t(`cs.channel.${ch}` as MessageKey)}</span>
                {granted ? (
                  <Button
                    variant="secondary"
                    data-testid={`channel-${ch}-off`}
                    disabled={busy}
                    onClick={() => withdraw.mutate(granted.id)}
                  >
                    {t("cs.turnOff")}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    data-testid={`channel-${ch}-on`}
                    disabled={busy || !marketingNotice || !profile?.partyId}
                    onClick={() =>
                      marketingNotice &&
                      grant.mutate({
                        noticeId: marketingNotice.id,
                        purposeId: MARKETING_PURPOSE,
                        channel: ch,
                      })
                    }
                  >
                    {t("cs.turnOn")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* THE WORST SILENT FAILURE IN THE PRODUCT. Grant and withdraw both fired
            and rendered nothing on rejection, so a customer who tapped "Kapat" on
            SMS marketing and hit a 500 walked away believing they had revoked a
            permission that was still live. On a KVKK control panel, silence is not
            a missing error message — it is a false statement about a legal right. */}
        <MutationError
          error={latestError(grant, withdraw)}
          fallback={t("cs.actionFailed")}
          offline={t("cs.offline")}
        />

        {/* WHY THE CHANNEL BUTTONS CAN BE DISABLED. Without a marketing notice on
            file there is nothing to consent TO, so granting would create a consent
            row bound to no disclosure. Previously the buttons were simply greyed
            with no explanation, which made the flagship no-spam screen look broken. */}
        {!marketingNotice && (
          <p className="muted small" role="note" data-testid="nospam-no-notice">
            {t("cs.noMarketingNotice")}
          </p>
        )}

        {/* The suppression record ("sizi aramadığımız anlar") needs a NEW party-realm read
            (contact/suppression history) that does NOT exist — flagged backend-first
            (§11). We ship the PROMISE, not a faked list. */}
        <div className="nospam-row" data-testid="suppression-flag">
          <div>
            <strong>{t("cs.suppressionTitle")}</strong>
            <p className="muted small">{t("cs.suppressionBody")}</p>
          </div>
          <Badge tone="neutral">{t("cs.soon")}</Badge>
        </div>
      </Card>

      {/* Fresh-auth is framed as protection: a quiet, calm line, never an error/code. */}
      {busy ? (
        <p className="muted" role="status" data-testid="stepup-note">
          {t("cs.stepupNote")}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The step's state, READ FROM THE LEDGER.
 *
 * This used to return an unconditional green "captured" for every required step
 * without ever touching `useConsents()` data — the screen asserted to the data
 * subject that a consent record existed when, for most steps, none does (the only
 * consent row the system writes today is `marketing`). Under KVKK that is the one
 * thing this screen must never do: claim a record it cannot produce.
 *
 * Now: a GRANTED row → captured (green). A withdrawn/absent row → an honest neutral
 * "not in your record" state. `granted` is resolved by the caller from the real rows.
 */
function StepState({ kind, granted, t }: { kind: StepKind; granted: boolean; t: TFn }) {
  if (kind === "marketing") return <Badge tone="neutral">{t("cs.state.manageBelow")}</Badge>;
  if (kind === "optional") return <Badge tone="neutral">{t("cs.state.optional")}</Badge>;
  if (granted) return <Badge tone="success">{t("cs.state.captured")}</Badge>;
  return <Badge tone="neutral">{t("cs.state.notRecorded")}</Badge>;
}

function LedgerRow({
  row,
  notice,
  t,
  busy,
  onWithdraw,
}: {
  row: ConsentRow;
  notice: Notice | undefined;
  t: TFn;
  busy: boolean;
  onWithdraw: () => void;
}) {
  return (
    <li className="consent-ledger-row" data-testid={`ledger-${row.id}`}>
      <div className="consent-ledger-main">
        <span className="consent-purpose">{purposeLabel(row.purposeId, t)}</span>
        <span className="muted small">{t(`cs.channel.${row.channel}` as MessageKey)}</span>
      </div>
      <div className="consent-ledger-meta">
        {notice ? (
          <Badge tone="neutral">
            {notice.code} v{notice.version}
          </Badge>
        ) : null}
        <span className="muted small" data-testid={`ledger-asof-${row.id}`}>
          {t("cs.asOf")} {asOf(row.grantedAt)}
        </span>
        <Button
          variant="secondary"
          data-testid={`ledger-withdraw-${row.id}`}
          disabled={busy}
          onClick={onWithdraw}
        >
          {t("cs.withdraw")}
        </Button>
      </div>
    </li>
  );
}
